import { useState, useEffect, useRef } from 'react'
import { ChatListSidebarItem } from '../../conversation_list/schema'
import { useAppDispatch } from '@/store/store-hooks'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import patchRenameAgentConversationTitle from '@/agent/conversations/fetch/patch-rename-agent-conversation-title'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'

type ChatTitleChangeDialogProps = {
  dialogVisible: boolean
  onDialogClose: () => void
  chatItem: ChatListSidebarItem | null
}

export default function ChatTitleChangeDialog(props: ChatTitleChangeDialogProps) {
  const { dialogVisible, chatItem, onDialogClose } = props
  const dispatch = useAppDispatch()
  const inputRef = useRef<HTMLInputElement>(null)

  // Local state
  const [newTitle, setNewTitle] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<boolean>(false)

  // Change the title of the chat
  const changeTitleHandler = async (newTitle: string) => {
    if (!chatItem) {
      throw new Error('Chat item is not set')
    }

    setLoading(true)
    setError(false)
    try {
      await patchRenameAgentConversationTitle({ conversationId: chatItem.id, title: newTitle })
      dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId: chatItem.matter_id, forceRefresh: true }))

      // Short delay before closing the dialog. Keep loading true until then so
      // the Submit button stays disabled and can't fire a duplicate request.
      setTimeout(() => {
        onDialogClose()
      }, 500)
    } catch (err) {
      console.error('Error while renaming chat:', err)
      setError(true)
      setLoading(false)
    }
  }

  // Reset the dialog if dialogVisible changes
  useEffect(() => {
    setError(false)
    setLoading(false)

    // Clear the newTitle
    setNewTitle('')

    // Focus on the input field when dialog becomes visible
    if (dialogVisible && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [dialogVisible])

  // Handle form submission
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    changeTitleHandler(newTitle)
  }

  return (
    <Modal open={dialogVisible} onOpenChange={(open) => !open && onDialogClose()}>
      <ModalContent
        title="Rename Chat"
        width="md"
        height="max-md"
        footer={
          <ModalFooterWithActions
            secondaryAction={{ label: 'Cancel', onClick: onDialogClose, disabled: loading }}
            primaryAction={{
              label: 'Submit',
              onClick: () => changeTitleHandler(newTitle),
              loading,
              disabled: !newTitle.trim(),
            }}
            inlineMessage={error ? { variant: 'error', children: 'Failed to rename chat. Please try again.' } : undefined}
          />
        }
      >
        <form onSubmit={handleFormSubmit}>
          <input
            type="text"
            ref={inputRef}
            className="w-full p-2 border border-brand-neutral-300 rounded-md focus:outline-none focus:ring-0 focus:border-brand-neutral-400"
            placeholder="Enter new title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
        </form>
      </ModalContent>
    </Modal>
  )
}
